import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, InputNumber, Row, Switch, Table, message } from 'antd';
import { getUnitSettings, upsertUnitSettings } from '../api/ownerReporting';
import { labelWithTooltip } from '../pages/servicePackages/formShared';
import {
  FOC_ROWS,
  emptyFixedOperationCost,
  isFocEmpty,
  mergeListingOperationCost,
  resolveLoadedFoc,
  setFocRate,
} from './unitSettingsFoc';

const CLEANING_FOC_TOOLTIP =
  'Cleaning based on reservations is the per-booking cleaning fee. It does not appear as a report expense (shows as 0). It is applied on each booking instead.';

const focInput = (foc, key, field, onChange) => {
  const rates = (foc && foc[key]) || {};
  return (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <span style={{ marginRight: 6, color: 'rgba(0,0,0,0.45)' }}>RM</span>
      <InputNumber
        min={0}
        step={0.01}
        precision={2}
        style={{ width: '100%' }}
        value={Number(rates[field]) || 0}
        onChange={val => onChange(key, field, val)}
      />
    </span>
  );
};

const UnitSettings = ({ unitId, hostId, title }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [listingOperationCost, setListingOperationCost] = useState(null);
  const [values, setValues] = useState({
    cleaningFeeEnabled: true,
    unitSqft: 0,
    monthlyRentalAmount: 0,
    fixedOperationCost: emptyFixedOperationCost(),
  });

  useEffect(() => {
    if (!unitId || !hostId) return undefined;
    let cancelled = false;
    setLoading(true);
    getUnitSettings(hostId, unitId)
      .then(data => {
        if (cancelled) return;
        const listing = (data && data.listingOperationCost) || null;
        setListingOperationCost(listing);
        setValues({
          cleaningFeeEnabled: !(data && data.cleaningFeeEnabled === false),
          unitSqft: (data && data.unitSqft) || 0,
          monthlyRentalAmount: (data && data.monthlyRentalAmount) || 0,
          fixedOperationCost: resolveLoadedFoc(data && data.fixedOperationCost, listing),
        });
      })
      .catch(err => {
        if (!cancelled) message.error((err && err.message) || 'Failed to load Owner Reporting unit settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [unitId, hostId]);

  const set = key => val => setValues(prev => ({ ...prev, [key]: val }));

  const onFocChange = (costKey, field, val) => {
    setValues(prev => ({
      ...prev,
      fixedOperationCost: setFocRate(prev.fixedOperationCost, costKey, field, val),
    }));
  };

  const onCopyFromListing = () => {
    if (isFocEmpty(listingOperationCost)) {
      message.info('This listing has no fixed operation costs');
      return;
    }
    setValues(prev => ({
      ...prev,
      fixedOperationCost: mergeListingOperationCost(listingOperationCost),
    }));
    message.success('Copied listing fixed operation costs. Save to apply.');
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await upsertUnitSettings(hostId, unitId, {
        cleaningFeeEnabled: values.cleaningFeeEnabled,
        unitSqft: values.unitSqft,
        monthlyRentalAmount: values.monthlyRentalAmount,
        fixedOperationCost: values.fixedOperationCost,
      });
      message.success('Owner Reporting settings saved');
    } catch (err) {
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!unitId || !hostId) {
    return (
      <Card title={title || 'Owner Reporting'}>
        <p style={{ marginBottom: 0, color: 'rgba(0,0,0,0.45)' }}>
          Unit host could not be resolved. Open this unit from Listings after Owner Reporting is enabled for the host.
        </p>
      </Card>
    );
  }

  const focColumns = [
    {
      title: 'Operation Cost Type',
      dataIndex: 'label',
      width: 160,
      render: (label, row) =>
        row && row.key === 'cleaning' ? labelWithTooltip(label, CLEANING_FOC_TOOLTIP) : label,
    },
    {
      title: 'Days of Reservations',
      dataIndex: 'perDay',
      render: (_v, row) => focInput(values.fixedOperationCost, row.key, 'perDay', onFocChange),
    },
    {
      title: 'Based on Reservations',
      dataIndex: 'perCheckOut',
      render: (_v, row) => focInput(values.fixedOperationCost, row.key, 'perCheckOut', onFocChange),
    },
    {
      title: 'Based on months',
      dataIndex: 'perMonth',
      render: (_v, row) => focInput(values.fixedOperationCost, row.key, 'perMonth', onFocChange),
    },
  ];

  return (
    <Card title={title || 'Owner Reporting'} loading={loading}>
      <Form layout="vertical">
        <Form.Item label="Include cleaning fee add-on">
          <Switch checked={values.cleaningFeeEnabled} onChange={set('cleaningFeeEnabled')} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Unit SQFT">
              <InputNumber style={{ width: '100%' }} min={0} value={values.unitSqft} onChange={set('unitSqft')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Monthly rental (Fixed Rental)">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                value={values.monthlyRentalAmount}
                onChange={set('monthlyRentalAmount')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Fixed Operation Costs"
          extra="Same rates as Listing → Unit. Cleaning based on reservations is the per-booking cleaning fee. Copy from listing fills the grid; Save stores Owner Reporting rates only."
        >
          <div style={{ marginBottom: 8 }}>
            <Button onClick={onCopyFromListing} disabled={isFocEmpty(listingOperationCost)}>
              Copy from listing
            </Button>
          </div>
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            bordered
            scroll={{ x: 700 }}
            columns={focColumns}
            dataSource={FOC_ROWS}
          />
        </Form.Item>
        <Button type="primary" onClick={onSave} loading={saving}>
          Save
        </Button>
      </Form>
    </Card>
  );
};

export default UnitSettings;
